import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api.js";
import { profileSchema, type ProfileInput } from "@mnp/shared";
import { TgHeader, Icon } from "../components/ui/index.js";
import { requestContact, isRequestContactSupported, hapticImpact } from "../lib/telegram.js";

export function ProfileEditScreen() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const { data: profile } = useQuery({
    queryKey: ["profile"],
    queryFn: () => api<any>("/profile"),
  });

  const [form, setForm] = useState<ProfileInput | null>(null);
  const current: ProfileInput = form ?? {
    fullName: profile?.fullName ?? "",
    phone: profile?.phone ?? "",
    // Единое поле документа. Старые профили хранят серию+номер раздельно —
    // склеиваем для отображения; при сохранении всё уходит в passportNumber.
    passportSeries: "",
    passportNumber: `${profile?.passportSeries ?? ""}${profile?.passportNumber ?? ""}`,
  };
  const [error, setError] = useState<string | null>(null);
  const [phoneFromTg, setPhoneFromTg] = useState(false);
  const [contactPending, setContactPending] = useState(false);
  const contactSupported = isRequestContactSupported();

  async function onShareContact() {
    setError(null);
    setContactPending(true);
    hapticImpact("light");
    try {
      const phone = await requestContact();
      if (phone) {
        setForm({ ...current, phone });
        setPhoneFromTg(true);
      } else {
        setError("Не удалось получить номер. Можно ввести вручную.");
      }
    } finally {
      setContactPending(false);
    }
  }

  const save = useMutation({
    mutationFn: (d: ProfileInput) => api("/profile", { method: "PUT", body: JSON.stringify(d) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["profile"] });
      nav("/profile", { replace: true });
    },
    onError: (e: any) => setError(e.message ?? "Ошибка сохранения"),
  });

  function submit(e: React.FormEvent) {
    e.preventDefault();
    const parsed = profileSchema.safeParse(current);
    if (!parsed.success) {
      setError(parsed.error.errors[0]?.message ?? "Проверьте поля");
      return;
    }
    save.mutate(parsed.data);
  }

  return (
    <div className="app-shell">
      <TgHeader title="Мои данные" onBack={() => nav("/profile")} />
      <form onSubmit={submit} className="screen pad">
        <h2 className="t-h2 mb-3">Редактирование</h2>
        <div className="field">
          <label>ФИО</label>
          <input
            className="input"
            value={current.fullName}
            onChange={(e) => setForm({ ...current, fullName: e.target.value })}
          />
        </div>
        <div className="field">
          <label>Телефон</label>
          {contactSupported && (
            <button
              type="button"
              className="btn btn-secondary btn-block mb-2"
              onClick={onShareContact}
              disabled={contactPending}
            >
              <Icon name="phone" size={16} />
              <span style={{ marginLeft: 6 }}>
                {contactPending ? "Запрашиваем…" : "Взять номер из Telegram"}
              </span>
            </button>
          )}
          <input
            className="input"
            value={current.phone}
            onChange={(e) => {
              setForm({ ...current, phone: e.target.value });
              if (phoneFromTg) setPhoneFromTg(false);
            }}
            inputMode="tel"
          />
          {phoneFromTg && (
            <div className="help-ok mt-1" style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <Icon name="check" size={14} />
              Подтверждён Telegram
            </div>
          )}
        </div>
        <div className="field">
          <label>Серия и номер документа</label>
          <input
            className="input"
            value={current.passportNumber}
            maxLength={11}
            inputMode="text"
            autoCapitalize="characters"
            onChange={(e) =>
              setForm({ ...current, passportSeries: "", passportNumber: e.target.value })
            }
            placeholder="9 или 11 символов"
          />
          <div className="t-small mt-1" style={{ color: "var(--mnp-fg-3)" }}>
            Номер/серия документа без пробелов (9 или 11 символов).
          </div>
        </div>
        {error && <div className="help-err">{error}</div>}
        {save.error && !error && (
          <div className="banner banner-danger mt-2" role="alert">
            <Icon name="alertCircle" size={18} />
            <div>
              {(save.error as any)?.body?.message ?? "Ошибка сети, попробуйте ещё раз"}
            </div>
          </div>
        )}
        <button type="submit" className="btn btn-primary btn-block btn-lg mt-3" disabled={save.isPending}>
          {save.isPending ? "Сохраняем…" : "Сохранить"}
        </button>
      </form>
    </div>
  );
}
